import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Layers } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { usePageSeo } from '../hooks/usePageSeo';
import { slugify } from '../lib/slugify';
import {
  useHowItWorksTopics,
  resolveScopeName,
  topicsInScope,
  groupBySubcategory,
} from '../lib/howItWorksHierarchy';
import FavoriteButton from '../components/favorites/FavoriteButton';
import { useFavoritedIds } from '../hooks/useFavoritedIds';

const TopicCard = ({ topic, favorited }) => (
  <Link to={`/how-it-works/${topic.topic_id}`} data-testid={`how-it-works-card-${topic.topic_id}`}>
    <Card className="technical-card h-full relative">
      <FavoriteButton
        contentType="how_it_works_topic"
        contentId={topic.topic_id}
        initialFavorited={favorited}
        title={topic.title}
        className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white"
      />
      <CardContent className="p-6 flex flex-col h-full">
        <h3 className="font-chivo text-lg font-bold mb-2 line-clamp-2">{topic.title}</h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2 flex-1">{topic.short_description}</p>
        <div className="flex items-center justify-end pt-4 border-t border-structure">
          <ChevronRight className="h-4 w-4 text-autoxium-blue" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

const TopicGrid = ({ topics, favoritedIds }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
    {topics.map((topic) => (
      <TopicCard key={topic.topic_id} topic={topic} favorited={favoritedIds.has(topic.topic_id)} />
    ))}
  </div>
);

// "More Topics" drill-down for a domain section (every topic in that domain, grouped
// under every one of its populated sub-sections, each with its own heading) or a single
// sub-section (its own full topic list, flat) — which mode applies is purely a function
// of whether `subcategorySlug` is present in the route, mirroring HowItWorksSectionPage.
// This is the full listing, not just what's left over after the scope's own "Top Topics"
// trending row — the back link always returns to the main "How Does It Work?" landing
// page, never to the intermediate section/subsection page, so it reads unambiguously
// regardless of how deep the scope is.
const HowItWorksMoreTopicsPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const navigate = useNavigate();
  const { topics, loading } = useHowItWorksTopics();

  const { categoryName, subcategoryName } = resolveScopeName(topics, categorySlug, subcategorySlug);
  const scopeTopics = useMemo(
    () => topicsInScope(topics, categorySlug, subcategorySlug),
    [topics, categorySlug, subcategorySlug]
  );
  const scopeName = subcategoryName || categoryName;
  const favoritedIds = useFavoritedIds('how_it_works_topic', scopeTopics.map((t) => t.topic_id));

  useEffect(() => {
    if (!loading && topics.length > 0 && !categoryName) {
      navigate('/how-it-works', { replace: true });
    }
  }, [loading, topics.length, categoryName, navigate]);

  usePageSeo(
    scopeName ? `More Topics — ${scopeName} | AUTOXIUM` : undefined,
    scopeName ? `All ${scopeName} topics on AUTOXIUM.` : undefined
  );

  const isLoading = loading || !scopeName;

  const subGroups = useMemo(() => {
    if (subcategorySlug || isLoading) return null;
    return groupBySubcategory(scopeTopics);
  }, [subcategorySlug, isLoading, scopeTopics]);
  const showSubHeaders = subGroups ? subGroups.some(([key]) => key !== null) : false;

  if (isLoading) {
    return (
      <div className="min-h-screen" data-testid="how-it-works-more-topics-loading">
        <div className="bg-chassis text-white py-16 md:py-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
            <Skeleton className="h-4 w-40 mb-6 bg-white/10" />
            <Skeleton className="h-10 w-1/2 bg-white/10" />
          </div>
        </div>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="how-it-works-more-topics-page">
      <div className="bg-chassis text-white py-16 md:py-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="back-to-how-it-works"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to How Does It Work?
          </Link>
          <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">More Topics</p>
          <h1 className="font-chivo text-3xl md:text-5xl font-black uppercase tracking-tighter">
            {scopeName}
          </h1>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {scopeTopics.length === 0 ? (
          <div className="text-center py-16" data-testid="how-it-works-more-topics-empty">
            <Layers className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Topics Yet</h3>
            <p className="text-muted-foreground">Check back soon for new {scopeName} topics.</p>
          </div>
        ) : subGroups ? (
          <div className="space-y-12">
            {subGroups.map(([subcategory, groupTopics]) => (
              <div
                key={subcategory || '_'}
                data-testid={
                  subcategory ? `more-topics-subcategory-${subcategory.toLowerCase().replace(/\s+/g, '-')}` : undefined
                }
              >
                {showSubHeaders &&
                  (subcategory ? (
                    <Link
                      to={`/how-it-works/section/${categorySlug}/${slugify(subcategory)}`}
                      className="inline-block font-chivo text-xs font-bold uppercase tracking-wider text-autoxium-blue mb-3 hover:underline"
                    >
                      {subcategory}
                    </Link>
                  ) : (
                    <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                      General
                    </h3>
                  ))}
                <TopicGrid topics={groupTopics} favoritedIds={favoritedIds} />
              </div>
            ))}
          </div>
        ) : (
          <TopicGrid topics={scopeTopics} favoritedIds={favoritedIds} />
        )}
      </div>
    </div>
  );
};

export default HowItWorksMoreTopicsPage;
